<template>
  <div class="optimized-image-container" >
    <!-- Placeholder mientras carga la imagen -->
 
    <!-- Imagen principal -->
    <img
      v-if="imagePath"
      :src="imagePath"
      :class="combinedClasses"
      :fetchpriority="priority"
      :loading="loadingStrategy"
      :alt="altText"
      @load="handleImageLoaded"
      @error="handleImageError"
      :style="[imageOpacityStyle, { 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover', 
        objectPosition: 'center',
        position: 'absolute',
        top: 0,
        left: 0
      }]"
    />
  </div>
</template>

<script>
export default {
  name: 'OptimizedImage',
  props: {
    imagePath: {
      type: String,
      required: true,
    },
    srcset: {
      type: String,
      default: '',
    },
    sizes: {
      type: String,
      default: '',
    },
    width: {
      type: [String, Number],
      default: null,
    },
    height: {
      type: [String, Number],
      default: null,
    },
    maxWidth: {
      type: String,
      default: '100%',
    },
    maxHeight: {
      type: String,
      default: '100%',
    },
    baseClass: {
      type: String,
      default: '',
    },
    additionalClasses: {
      type: String,
      default: 'mt-0 px-0 mx-0 rounded-xl',
    },
    blurOnHide: {
      type: Boolean,
      default: false,
    },
    showImage: {
      type: Boolean,
      default: true,
    },
    priority: {
      type: String,
      default: 'high',
      validator: (value) => ['high', 'low', 'auto'].includes(value),
    },
    loadingStrategy: {
      type: String,
      default: 'eager',
      validator: (value) => ['eager', 'lazy'].includes(value),
    },
    altText: {
      type: String,
      default: 'Imagen de contenido',
    },
    placeholderColor: {
      type: String,
      default: '#f0f0f0',
    },
    aspectRatio: {
      type: [String, Number],
      default: null,
    },
    preload: {
      type: Boolean,
      default: false,
    },
    transitionDuration: {
      type: Number,
      default: 300,
    },
    lowQualityPreview: {
      type: String,
      default: '',
    }
  },
  
  data() {
    return {
      isLoaded: false,
      imageError: false,
    };
  },

  computed: {
    combinedClasses() {
      return [
        this.baseClass,
        this.additionalClasses,
        { 'blur-explicit': !this.showImage && this.blurOnHide },
      ];
    },
    
    showPlaceholder() {
      return !this.isLoaded;
    },

    placeholderStyle() {
      return {
        backgroundColor: this.placeholderColor,
        backgroundImage: this.lowQualityPreview ? `url(${this.lowQualityPreview})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
      };
    },

    imageOpacityStyle() {
      return {
        transition: `opacity ${this.transitionDuration}ms ease-in-out`,
        opacity: this.isLoaded ? 1 : 0,
      };
    },

    srcsetValue() {
      return this.srcset || null;
    },

    sizesValue() {
      return this.sizes || null;
    },
  },
  
  mounted() {
    if (this.preload && this.priority === 'high') {
      this.preloadImage();
    }
  },

  methods: {
    handleImageLoaded(event) {
      this.isLoaded = true;
      this.$emit('image-loaded', event);
      
      if (window.performance && window.performance.mark) {
        window.performance.mark('image-loaded');
        
        // Core Web Vitals: Si es una imagen crítica, medimos su impacto en LCP
        if (this.priority === 'high') {
          window.performance.measure('critical-image-load', 'navigationStart', 'image-loaded');
        }
      }
    },
    
    handleImageError(error) {
      this.imageError = true;
      this.$emit('image-error', error);
      console.warn(`Error loading image: ${this.imagePath}`, error);
    },

    preloadImage() {
      // Precargar imágenes críticas
      if (this.imagePath) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = this.imagePath;
        document.head.appendChild(link);
      }
    }
  },
}
</script>

<style scoped>
.optimized-image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blur-explicit {
  filter: blur(10px);
}

.image-placeholder {
  border-radius: inherit;
}
</style> 